import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, X } from 'lucide-react';

const NotificationContext = createContext(null);

let idCounter = 0;

export const NotificationProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState({ active: false, message: '' });

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((message, type) => {
    const id = ++idCounter;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => removeToast(id), 4000);
  }, [removeToast]);

  const notifySuccess = useCallback((message) => addToast(message, 'success'), [addToast]);
  const notifyError = useCallback((message) => addToast(message, 'error'), [addToast]);

  const showLoading = useCallback((message = 'Loading…') => setLoading({ active: true, message }), []);
  const hideLoading = useCallback(() => setLoading({ active: false, message: '' }), []);

  return (
    <NotificationContext.Provider value={{ notifySuccess, notifyError, showLoading, hideLoading }}>
      {children}

      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 3000, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {toasts.map(t => (
          <div
            key={t.id}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              background: 'var(--surface-color)', color: 'var(--text-main)',
              padding: '14px 16px', borderRadius: '10px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              borderLeft: `4px solid ${t.type === 'success' ? 'var(--success-color)' : 'var(--danger-color)'}`,
              minWidth: '280px', maxWidth: '380px', animation: 'slideUp 0.25s ease-out'
            }}
          >
            {t.type === 'success'
              ? <CheckCircle size={20} color="var(--success-color)" style={{ flexShrink: 0 }} />
              : <XCircle size={20} color="var(--danger-color)" style={{ flexShrink: 0 }} />}
            <span style={{ flex: 1, fontSize: '14px' }}>{t.message}</span>
            <button
              onClick={() => removeToast(t.id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px', flexShrink: 0 }}
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>

      {loading.active && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000 }}>
          <div style={{ background: 'var(--surface-color)', padding: '28px 36px', borderRadius: '14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', boxShadow: '0 12px 32px rgba(0,0,0,0.2)' }}>
            <div className="spinner" />
            <span style={{ fontSize: '14px', color: 'var(--text-main)', fontWeight: '500' }}>{loading.message}</span>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotification must be used within NotificationProvider');
  return ctx;
};
